import React, { useState, useEffect } from 'react';
import { MessageSquare, Trash2 } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import TechnologyLikeButton from '../technology/community/TechnologyLikeButton';
import { formatTechnologyDate } from '../../services/technologyCommunityService';
import {
  addComment,
  deleteComment,
  toggleCommentLike,
  subscribeToComments,
} from '../../services/firebase/community';
import {
  COMMUNITY_TYPE_LABELS,
  COMMUNITY_PILLAR_LABELS,
  getPostAuthorAvatarUrl,
} from '../../utils/communityConstants';
import CommunityAttachmentThumb from './CommunityAttachmentThumb';
import CommunityCommentItem from './CommunityCommentItem';

const CommunityPostCard = ({ post, onLike, onDelete, onOpenAttachment }) => {
  const { currentUser } = useAuth();
  const auth = getAuth();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [newReply, setNewReply] = useState('');

  const isAuthor = post.authorId === currentUser?.uid;
  const typeLabel = post.type && post.type !== 'general' ? COMMUNITY_TYPE_LABELS[post.type] : null;
  const pillarLabel = COMMUNITY_PILLAR_LABELS[post.category] || post.category;

  useEffect(() => {
    const unsubscribe = subscribeToComments(post.id, setComments);
    return () => unsubscribe();
  }, [post.id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await addComment(post.id, { content: newComment });
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment. Please try again.');
    }
  };

  const handleAddReply = async (postId, commentId) => {
    if (!newReply.trim()) return;
    try {
      await addComment(postId, { content: newReply, parentCommentId: commentId });
      setNewReply('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Error adding reply:', err);
      alert('Failed to add reply. Please try again.');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await toggleCommentLike(commentId);
    } catch (err) {
      console.error('Error toggling comment like:', err);
      alert('Failed to update comment like. Please try again.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const inputClass =
    'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

  return (
    <article className="rounded-2xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm p-5 w-full transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3 mb-3">
        <img
          src={getPostAuthorAvatarUrl(post)}
          alt={`${post.author}'s avatar`}
          className="w-10 h-10 rounded-full object-cover shrink-0 ring-2 ring-white dark:ring-gray-900 shadow-sm"
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author || 'User')}&background=ef4444&color=ffffff&size=40`;
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="font-semibold text-sm text-gray-900 dark:text-white">
              {post.author}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {post.createdAt ? formatTechnologyDate(post.createdAt) : 'Unknown date'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {pillarLabel && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                {pillarLabel}
              </span>
            )}
            {typeLabel && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800/60">
                {typeLabel}
              </span>
            )}
          </div>
        </div>

        {isAuthor && (
          <button
            type="button"
            onClick={() => onDelete?.(post.id)}
            className="text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors p-1.5 shrink-0"
            title="Delete post"
            aria-label="Delete post"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <h3 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">{post.title}</h3>

      <p className="text-sm mb-4 leading-relaxed whitespace-pre-wrap text-gray-700 dark:text-gray-300">
        {post.description}
      </p>

      {post.files?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.files.map((file, index) => (
            <button
              type="button"
              key={index}
              className="rounded-xl border overflow-hidden focus:outline-none focus:ring-2 focus:ring-red-500 border-gray-200 dark:border-gray-700 shadow-sm transition-transform hover:scale-[1.03]"
              onClick={(e) => onOpenAttachment?.(file, e)}
              aria-label="Open attachment"
            >
              <CommunityAttachmentThumb file={file} className="h-16 w-16 object-cover shrink-0" />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-gray-800">
        <TechnologyLikeButton
          likes={post.likes}
          likedBy={post.likedBy}
          onToggle={() => onLike?.(post.id)}
        />
        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <MessageSquare className="w-4 h-4" />
          {comments.length}
        </span>
      </div>

      <div className="pt-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className={inputClass}
              rows="2"
            />
          </div>
          <button
            type="button"
            onClick={handleAddComment}
            disabled={!newComment.trim()}
            className="px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            Comment
          </button>
        </div>

        {comments.length > 0 && (
          <div className="space-y-4">
            {comments.map((comment) => (
              <CommunityCommentItem
                key={comment.id}
                comment={comment}
                postId={post.id}
                onLike={handleLikeComment}
                onDelete={handleDeleteComment}
                onReply={setReplyingTo}
                replyingTo={replyingTo}
                newReply={newReply}
                setNewReply={setNewReply}
                handleAddReply={handleAddReply}
                auth={auth}
                depth={0}
                isTopLevel
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default CommunityPostCard;
